// customer-address-management.component.ts
import {Component, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {AuthService, DotLoadingServices} from '../../../../../../core';
import {NotificationService} from "../../../../../services/notification.service";
import {CustomerManagementService} from '../../services/customers.service';
import {CustomerAddressDTO} from '../../interfaces/customer.entity';
import {SearchCountryComponent} from '../../../../../shared';
import {CustomerModalStateService} from '../../services/customer-modal-state.service';

@Component({
  selector: 'app-customer-address-management',
    imports: [
        FormsModule,
        SearchCountryComponent,
    ],
  templateUrl: './customer-address-management.component.html',
  styleUrl: './customer-address-management.component.scss',
  standalone: true
})
export class CustomerAddressManagementComponent implements OnInit, OnDestroy {
  
  protected customerService = inject(CustomerManagementService);
  protected authService = inject(AuthService);
  protected notification = inject(NotificationService);
  protected loading = inject(DotLoadingServices);
  protected router = inject(Router);
  protected modalState = inject(CustomerModalStateService);

  resetSearchTrigger = signal<boolean>(false);
  protected currentCustomerId = signal<number | null>(null);
  protected customerName = signal<string>('');
  
  // Separate arrays for existing and new addresses
  protected existingAddresses = signal<CustomerAddressDTO[]>([]);
  protected newAddresses = signal<CustomerAddressDTO[]>([]);

  ngOnInit(): void {
    const activeCustomer = this.customerService.active();
    if (!activeCustomer) {
      this.notification.set({
        type: 'alert',
        message: 'No customer selected. Redirecting...',
        timeout: 3000
      });
      this.router.navigate(['/customer/customer-grid']);
      return;
    }

    this.currentCustomerId.set(activeCustomer.customerId);
    this.customerName.set(activeCustomer.customerName);
    this.loadExistingAddresses(activeCustomer);
  }

  private loadExistingAddresses(customer: any): void {
    const addresses = this.mapAddressesFromAPI(customer.addresses || []);    
    this.existingAddresses.set(addresses);
  }

  private mapAddressesFromAPI(addresses: any[]): CustomerAddressDTO[] {
    return addresses.map(addr => ({
      addressId: addr.addressId,
      addressLineOne: addr.addressLineOne || '',
      addressLineTwo: addr.addressLineTwo || '',
      addressLineThree: addr.addressLineThree || '',
      country: addr.countryOb?.countryId || -1,
      show: addr.city || '',
      identityRef: addr.identityRef || '',
      contactNo: addr.contactNo || '',
      useForInvoice: addr.useForInvoice === 1,
      isDefaultInvoice: addr.isDefaultInvoice === 1,
      useForDelivery: addr.useForDelivery === 1,
      isDefaultDelivery: addr.isDefaultDelivery === 1,
      status: addr.status
    }));
  }

  // Add new address to the new addresses list
  protected addNewAddress(): void {
    const newAddress: CustomerAddressDTO = {
      addressLineOne: '',
      addressLineTwo: '',
      addressLineThree: '',
      country: -1,
      show: '',
      identityRef: '',
      contactNo: '',
      useForInvoice: false,
      isDefaultInvoice: false,
      useForDelivery: false,
      isDefaultDelivery: false,
      status:false,
    };
    this.newAddresses.update(current => [...current, newAddress]);
  }

  protected removeNewAddress(index: number): void {
    this.newAddresses.update(current => current.filter((_, i) => i !== index));
  }

  protected updateNewAddressField(index: number, field: keyof CustomerAddressDTO, value: any): void {
    this.newAddresses.update(current => {
      const updated = [...current];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }

  protected onNewAddressCountrySelected(index: number, value: any): void {
    this.updateNewAddressField(index, 'country', value);
  }

  protected onNewDeliveryDefaultChange(index: number, value: boolean): void {
    if (value) {
      this.newAddresses.update(current => {
        const updated = current.map((addr, i) => ({
          ...addr,
          isDefaultDelivery: i === index
        }));
        return updated;
      });
    } else {
      this.updateNewAddressField(index, 'isDefaultDelivery', false);
    }
  }

  protected isValidNewAddresses(): boolean {
    const addresses = this.newAddresses();
    
    if (addresses.length === 0) {
      return true; // Disabled if no new addresses
    }

    return !addresses.every(addr => {
      return addr.addressLineOne?.trim() && 
             addr.addressLineTwo?.trim() && 
             addr.show?.trim() && 
             addr.country !== -1;
    });
  }

  protected async saveNewAddresses(): Promise<void> {
    if (this.isValidNewAddresses()) {
      this.notification.set({
        type: 'alert',
        message: 'Please fill all required fields for new addresses.',
        timeout: 3000
      });
      return;
    }

    const customerId = this.currentCustomerId();
    if (!customerId) {
      this.notification.set({
        type: 'alert',
        message: 'Customer ID not found.',
        timeout: 3000
      });
      return;
    }

    try {
      this.loading.set(true);

      const formattedAddresses = this.newAddresses().map(address => ({
        addressLineOne: address.addressLineOne,
        addressLineTwo: address.addressLineTwo,
        addressLineThree: address.addressLineThree || '',
        show: address.show,
        country: address.country,
        identityRef: address.identityRef || '',
        contactNo: address.contactNo || '',
        useForInvoice: address.useForInvoice ? 1 : 0,
        useForDelivery: address.useForDelivery ? 1 : 0,
        isDefaultInvoice: address.isDefaultInvoice ? 1 : 0,
        isDefaultDelivery: address.isDefaultDelivery ? 1 : 0,
        status:address.status
      }));

      const payload = {
        createdBy: this.authService.username(),
        addresses: formattedAddresses
      };

      await firstValueFrom(
        this.customerService.updateAddresses(customerId, payload)
      );

      this.notification.set({
        type: 'success',
        message: 'New addresses added successfully.',
      });

      // Refresh the customer data and reload
      await this.refreshCustomerData();

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to add new addresses.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  private async refreshCustomerData(): Promise<void> {
    const customerId = this.currentCustomerId();
    if (!customerId) return;

    try {
      // Assuming you have a method to fetch customer by ID
      const customer = await firstValueFrom(
        this.customerService.getById(customerId)
      );
      
      this.loadExistingAddresses(customer.data);
      this.newAddresses.set([]);
      // this.customerService.notifyCustomerCreated();
    } catch (error) {
      console.error('Failed to refresh customer data:', error);
    }
  }

  protected goBack(): void {
     this.modalState.updateModal.set(true);
    this.modalState.generalUpdateModal.set(true);
    this.modalState.contactPersonspdateModal.set(false);
    this.modalState.addressesUpdateModal.set(false);
  }

      toggleAddressStatus(address: any): void {
  const newStatus = address.status ? 0 : 1;
    this.updateAddressStatus(address.addressId, newStatus);
}

// Add this method to call the API
async updateAddressStatus(addressId: number, isActive: number) {

      try {
      this.loading.set(true);

  const payload = {
    isActive: isActive,
    updatedBy: this.authService.username(),
  };

      await firstValueFrom(
        this.customerService.toggleAddressStatus(addressId,payload)
      );

      this.notification.set({
        type: 'success',
        message: 'Address Status changed successfully.',
      });

      // Refresh the customer data and reload
      await this.refreshCustomerData();

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to change Address Status.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }

}

// Method to update existing address fields
updateExistingAddressField(addressId: number, field: string, value: any): void {
  const addressIndex = this.existingAddresses().findIndex(a => a.addressId === addressId);
  if (addressIndex !== -1) {
    const updatedAddresses = [...this.existingAddresses()];
    updatedAddresses[addressIndex] = {
      ...updatedAddresses[addressIndex],
      [field]: value
    };
    
    // If disabling useForInvoice, also disable isDefaultInvoice
    if (field === 'useForInvoice' && !value) {
      updatedAddresses[addressIndex].isDefaultInvoice = false;
    }
    
    // If disabling useForDelivery, also disable isDefaultDelivery
    if (field === 'useForDelivery' && !value) {
      updatedAddresses[addressIndex].isDefaultDelivery = false;
    }
    
    this.existingAddresses.set(updatedAddresses);
  }
}

  protected async updateExistingAddress(address: any): Promise<void> {
    const customerId = this.currentCustomerId();
    if (!customerId) {
      this.notification.set({
        type: 'alert',
        message: 'Customer ID not found.',
        timeout: 3000
      });
      return;
    }

    try {
      this.loading.set(true);

      const formattedAddress = {
        addressId:address.addressId,
        addressLineOne: address.addressLineOne,
        addressLineTwo: address.addressLineTwo,
        addressLineThree: address.addressLineThree || '',
        show: address.show,
        country: address.country,
        identityRef: address.identityRef || '',
        contactNo: address.contactNo || '',
        useForInvoice: address.useForInvoice ? 1 : 0,
        useForDelivery: address.useForDelivery ? 1 : 0,
        isDefaultInvoice: address.isDefaultInvoice ? 1 : 0,
        isDefaultDelivery: address.isDefaultDelivery ? 1 : 0,
        status:address.status
      }

      const payload = {
        ...formattedAddress,
        createdBy: this.authService.username(),
      };

      await firstValueFrom(
        this.customerService.updateExistingAddress(payload.addressId, payload)        
      );

      this.notification.set({
        type: 'success',
        message: 'Address updated successfully.',
      });

      // Refresh the customer data and reload
      await this.refreshCustomerData();

    } catch (error) {
      this.notification.set({
        type: 'error',
        message: 'Failed to update address.',
      });
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }
  ngOnDestroy(): void {
    this.newAddresses.set([]);
    this.resetSearchTrigger.set(false);
  }
}